// botAsistente.utils.ts — funciones puras del panel «Asistente de WhatsApp» (round 2026-10-30, etapa B3).
//
// Todo lo que decide algo vive aquí y no en los componentes, para probarlo sin renderizar nada:
//   · la hora chilena de lo que muestra la pantalla,
//   · la cuenta regresiva de los modos de prueba,
//   · el borrador del formulario de modelos y límites, su validación (espejo de lo que exige el servidor) y la lista de
//     claves que REALMENTE cambiaron (el PUT manda solo esas),
//   · cómo se lee lo que el asistente habría guardado en una simulación,
//   · los nombres en español de los menús, códigos y pasos que muestra la actividad (el mapa vive en nombresBitacora.ts),
//   · los colores de las etiquetas (con contraste suficiente),
//   · la comparación de la frase de confirmación.
import type { CSSProperties } from "react";
import dayjs from "dayjs";
import { TZ_CHILE } from "../../../utils/fechaChile";
import { NOMBRE_OTRO } from "./nombresBitacora";
import type {
  BotSimulacion,
  BotSimulacionGasto,
  NombreTareaIA,
  TwilioAjustes,
  TwilioSettingsCambio,
} from "../../../interfaces/botAsistente.interfaces";

// ─── Fechas (siempre hora chilena) ───────────────────────────────────────────

/** `2026-10-30T15:05:00Z` → `30-10-2026 12:05` en hora de Chile. «—» si no es una fecha. */
export function formatoChile(iso: string | null | undefined): string {
  if (typeof iso !== "string" || iso === "") return "—";
  const d = dayjs(iso);
  if (!d.isValid()) return "—";
  return d.tz(TZ_CHILE).format("DD-MM-YYYY HH:mm");
}

/** `2026-10-30` → `30-10-2026` (un día calendario NO se convierte de zona: es el día que se anotó). `null` si no es un día. */
export function formatoDia(dia: string | null | undefined): string | null {
  if (typeof dia !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dia);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

/**
 * Cuánto falta para un vencimiento SEGÚN EL RELOJ DEL DISPOSITIVO. `vencido` es true si ya pasó (o la fecha no es válida).
 *
 * Es solo informativo: quien dice si un modo de prueba sigue encendido es el SERVIDOR (su booleano `...Activo`). Un reloj
 * adelantado o atrasado no puede dar por apagado un modo que el servidor tiene encendido, ni al revés; esta cuenta solo
 * sirve para mostrar «vence en ~N min» y para saber cuándo volver a preguntarle al servidor.
 */
export function tiempoRestante(hastaIso: string | null | undefined, ahoraMs: number): { vencido: boolean; texto: string } {
  const hasta = typeof hastaIso === "string" ? dayjs(hastaIso) : null;
  if (!hasta || !hasta.isValid()) return { vencido: true, texto: "" };
  const faltanMs = hasta.valueOf() - ahoraMs;
  if (faltanMs <= 0) return { vencido: true, texto: "" };
  const minutosTotales = Math.floor(faltanMs / 60_000);
  if (minutosTotales < 1) return { vencido: false, texto: "menos de 1 min" };
  const horas = Math.floor(minutosTotales / 60);
  const minutos = minutosTotales % 60;
  if (horas === 0) return { vencido: false, texto: `${minutos} min` };
  return { vencido: false, texto: `${horas} h ${minutos} min` };
}

// ─── Números y textos ───────────────────────────────────────────────────────

export function pluralizar(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural;
}

const CLP = new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 });

/** `12345` → `$12.345`. «—» si no es un número. */
export function formatoMonto(n: unknown): string {
  return typeof n === "number" && Number.isFinite(n) ? CLP.format(n) : "—";
}

/** `850` → `850 ms`; `1234` → `1,2 s`. «—» si no hay dato. */
export function formatoDuracion(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
}

/** Recorta un texto largo que viene del servidor para que no desarme la pantalla. Es solo presentación. */
export function acortar(texto: string, max = 200): string {
  return texto.length > max ? `${texto.slice(0, max - 1)}…` : texto;
}

/** ¿La persona escribió la frase pedida? Exacta (mayúsculas incluidas); solo se perdonan espacios en los bordes. */
export function fraseCoincide(escrito: string, frase: string): boolean {
  return escrito.trim() === frase;
}

// ─── Etiquetas de la actividad ───────────────────────────────────────────────

export interface EtiquetaColor {
  texto: string;
  color: string;
  /** Una línea para quien no es técnico. */
  ayuda: string;
}

/**
 * Color del TEXTO de una etiqueta de AntD, para que se lea (WCAG AA: 4,5:1 o más sobre el fondo claro de la etiqueta).
 * Los tonos que AntD usa por defecto (nivel 7 de cada paleta) no llegan: verde 3,37:1, dorado 2,76:1 y volcán 4,37:1.
 * Aquí va el nivel que sí llega (verde 8, dorado 9, volcán 8). Azul y rojo ya cumplen y no se tocan. La prueba de
 * botAsistente.utils.test.ts calcula el contraste con la fórmula de WCAG contra los fondos reales de AntD.
 */
export const COLOR_TEXTO_ETIQUETA: Record<string, string> = {
  green: "#237804",
  gold: "#874d00",
  volcano: "#ad2102",
};

/** Estilo en línea que sube el contraste del texto de una etiqueta de color; vacío si ese color ya se lee bien. */
export function estiloTextoEtiqueta(color: string): CSSProperties {
  const texto = COLOR_TEXTO_ETIQUETA[color];
  return texto ? { color: texto } : {};
}

const ETIQUETAS_RESULTADO: Record<string, EtiquetaColor> = {
  ok: { texto: "Correcto", color: "green", ayuda: "El mensaje se procesó bien." },
  simulado: { texto: "Simulado", color: "blue", ayuda: "Se revisó como uno real, pero no se guardó nada (modo simulación)." },
  error: { texto: "Con error", color: "red", ayuda: "Algo falló al procesar el mensaje." },
  incierto: { texto: "Incierto", color: "gold", ayuda: "No se pudo confirmar si se guardó: conviene revisarlo." },
  bloqueado: { texto: "Bloqueado", color: "volcano", ayuda: "Se rechazó: el número no tiene permiso o escribió demasiado rápido." },
  ignorado: { texto: "Ignorado", color: "default", ayuda: "El mensaje no se procesó (por ejemplo, no era para el asistente)." },
  // El servidor agrupa como «otro» lo que no calza con ninguno de los resultados conocidos (nunca lo deja pasar crudo).
  otro: { texto: NOMBRE_OTRO, color: "default", ayuda: "" },
};

/** Etiqueta de un resultado. Un valor desconocido se muestra tal cual, en gris: mejor feo y honesto que un rótulo que lo esconda. */
export function etiquetaResultado(resultado: string | null | undefined): EtiquetaColor {
  const clave = typeof resultado === "string" ? resultado : "";
  return ETIQUETAS_RESULTADO[clave] ?? { texto: clave || "—", color: "default", ayuda: "" };
}

export const RESULTADOS_FILTRO = ["ok", "simulado", "error", "incierto", "bloqueado", "ignorado"] as const;

export function textoOrigen(origen: string | null | undefined): string {
  if (origen === "whatsapp") return "WhatsApp";
  if (origen === "simulador") return "Simulador";
  if (origen === "otro") return NOMBRE_OTRO;
  return origen || "—";
}

export function textoTipoMensaje(tipo: string | null | undefined): string {
  switch (tipo) {
    case "texto":
      return "Texto";
    case "imagen":
      return "Imagen";
    case "audio":
      return "Audio";
    case "otro":
      return "Otro";
    default:
      return tipo || "—";
  }
}

/**
 * Aviso de privacidad cuando el texto de los mensajes SE ESTÁ guardando (lo que dice el servidor). Se muestra en «Modelos y
 * límites» (donde se enciende) y arriba de la tabla de «Actividad reciente» (donde se vería ese texto).
 */
export const AVISO_TEXTO_GUARDADO =
  "Se está guardando el texto de los mensajes de WhatsApp: pueden contener datos personales. Apágalo cuando termines de probar.";

// ─── Menús, códigos y pasos con nombre en español ────────────────────────────────
//
// Viven en nombresBitacora.ts (el ÚNICO mapa, vigilado por nombresBitacora.antiDeriva.test.ts: falla si el bot o el servidor
// estrenan un valor sin nombre). Se re-exportan aquí para que quien ya importaba de este archivo siga igual.
export {
  ETIQUETA_MENSAJES_AGRUPADOS,
  MENUS_FILTRO,
  NOMBRE_OTRO,
  NOMBRES_CODIGO,
  NOMBRES_MENU,
  NOMBRES_ROL,
  NOMBRES_SUB_ESTADO,
  filasDelDetalle,
  resumenDeAgregado,
  textoCodigo,
  textoMenu,
  textoRol,
  textoSubEstado,
  textoVentana,
} from "./nombresBitacora";

// ─── Modos de prueba: qué mostrar ────────────────────────────────────────────

export type VistaModoPrueba =
  /** El servidor lo da por encendido; `restante` es la cuenta regresiva según el reloj de este dispositivo (vacía si no se puede calcular). */
  | { tipo: "encendido"; restante: string }
  /** El servidor lo da por encendido, pero el reloj de este dispositivo ya pasó el vencimiento: se vuelve a preguntar al servidor. */
  | { tipo: "reloj_pasado" }
  /** El servidor lo da por apagado y trae la hora en que dejó de valer: venció. */
  | { tipo: "vencido" }
  | { tipo: "apagado" };

/**
 * Qué mostrar de un modo de prueba. MANDA EL SERVIDOR (`activoServidor`): el reloj del dispositivo solo informa. Con el reloj
 * adelantado, un modo que el servidor tiene encendido seguía mostrándose «Vencido»; con el atrasado, uno que el servidor ya
 * apagó seguía «encendido». «Vencido» solo sale cuando el servidor dice que está apagado y todavía trae su vencimiento.
 */
export function vistaModoPrueba(activoServidor: boolean, hastaIso: string | null | undefined, ahoraMs: number): VistaModoPrueba {
  const hayFecha = typeof hastaIso === "string" && dayjs(hastaIso).isValid();
  if (activoServidor) {
    if (!hayFecha) return { tipo: "encendido", restante: "" };
    const r = tiempoRestante(hastaIso, ahoraMs);
    return r.vencido ? { tipo: "reloj_pasado" } : { tipo: "encendido", restante: r.texto };
  }
  return hayFecha ? { tipo: "vencido" } : { tipo: "apagado" };
}

// ─── Simulaciones: qué habría guardado ───────────────────────────────────────

const esGastoSimulado = (g: unknown): g is BotSimulacionGasto =>
  typeof g === "object" && g !== null && typeof (g as BotSimulacionGasto).monto === "number";

/** Los gastos que el asistente habría guardado, tal como los calculó el servidor. Vacío si no hay detalle. */
export function gastosDeSimulacion(sim: BotSimulacion): BotSimulacionGasto[] {
  const lista = sim.resultado?.respuesta?.gastos;
  return Array.isArray(lista) ? lista.filter(esGastoSimulado) : [];
}

/** Cuántos registros traía la simulación aunque no haya detalle de gastos (respaldo para una fila antigua). */
export function cantidadRegistrosSimulacion(sim: BotSimulacion): number {
  const registros = sim.payload?.registros;
  return Array.isArray(registros) ? registros.length : 0;
}

// ─── Borrador del formulario «Modelos y límites» ─────────────────────────────

export interface BorradorTarea {
  model: string;
  /** Marcado = «no enviar temperatura» (el servidor la guarda como null). */
  sinTemperatura: boolean;
  /** Se conserva aunque `sinTemperatura` esté marcado, para volver al número anterior al desmarcar. */
  temperature: number | null;
}

export interface BorradorAjustes {
  tareas: Record<NombreTareaIA, BorradorTarea>;
  /** El servidor guarda milisegundos; la persona lo ve en segundos. `null` = el campo está vacío. */
  timeoutSeg: number | null;
  sinTopeTokens: boolean;
  maxTokens: number | null;
  limite: number | null;
  guardarTexto: boolean;
}

/** Con qué número arranca el campo de tokens cuando el ajuste actual es «sin tope» y la persona lo desmarca. */
export const TOKENS_INICIAL_AL_DESMARCAR = 1000;

const aBorradorTarea = (t: { model: string; temperature: number | null }): BorradorTarea => ({
  model: t.model,
  sinTemperatura: t.temperature === null,
  temperature: t.temperature ?? 0,
});

export function aBorrador(a: TwilioAjustes): BorradorAjustes {
  return {
    tareas: {
      "gasto.parse": aBorradorTarea(a.tareas["gasto.parse"]),
      "gasto.correct": aBorradorTarea(a.tareas["gasto.correct"]),
    },
    timeoutSeg: Math.round(a.timeoutMs / 1000),
    sinTopeTokens: a.maxTokensSalida === null,
    maxTokens: a.maxTokensSalida ?? TOKENS_INICIAL_AL_DESMARCAR,
    limite: a.limiteMensajesPorMinuto,
    guardarTexto: a.guardarTextoEventos,
  };
}

/** Mismos patrones y rangos que el servidor (si cambian allá, el servidor responde 400 con su mensaje). */
export const MODELO_PATRON = /^[A-Za-z0-9._:/-]+$/;
export const MODELO_MAX = 80;
export const TEMPERATURA_MIN = 0;
export const TEMPERATURA_MAX = 2;
export const TIMEOUT_SEG_MIN = 1;
export const TIMEOUT_SEG_MAX = 120;
export const TOKENS_MIN = 100;
export const TOKENS_MAX = 8000;
export const LIMITE_MIN = 1;
export const LIMITE_MAX = 60;

const esEntero = (n: unknown): n is number => typeof n === "number" && Number.isInteger(n);

export type ErroresBorrador = Partial<
  Record<
    | "gasto.parse.model"
    | "gasto.parse.temperature"
    | "gasto.correct.model"
    | "gasto.correct.temperature"
    | "timeoutSeg"
    | "maxTokens"
    | "limite",
    string
  >
>;

/** Lo que está mal en el borrador, campo por campo (vacío = todo bien). */
export function erroresDelBorrador(b: BorradorAjustes): ErroresBorrador {
  const e: ErroresBorrador = {};
  for (const tarea of ["gasto.parse", "gasto.correct"] as const) {
    const t = b.tareas[tarea];
    const modelo = t.model.trim();
    if (modelo === "") {
      e[`${tarea}.model`] = "Escribe el nombre del modelo.";
    } else if (modelo.length > MODELO_MAX) {
      e[`${tarea}.model`] = `Máximo ${MODELO_MAX} caracteres.`;
    } else if (!MODELO_PATRON.test(modelo)) {
      e[`${tarea}.model`] = "Solo letras, números y . _ : / - (sin espacios).";
    }
    if (!t.sinTemperatura) {
      const n = t.temperature;
      if (typeof n !== "number" || !Number.isFinite(n) || n < TEMPERATURA_MIN || n > TEMPERATURA_MAX) {
        e[`${tarea}.temperature`] = `Un número entre ${TEMPERATURA_MIN} y ${TEMPERATURA_MAX}, o marca «No enviar temperatura».`;
      }
    }
  }
  if (!esEntero(b.timeoutSeg) || b.timeoutSeg < TIMEOUT_SEG_MIN || b.timeoutSeg > TIMEOUT_SEG_MAX) {
    e.timeoutSeg = `Un número entero de segundos entre ${TIMEOUT_SEG_MIN} y ${TIMEOUT_SEG_MAX}.`;
  }
  if (!b.sinTopeTokens && (!esEntero(b.maxTokens) || b.maxTokens < TOKENS_MIN || b.maxTokens > TOKENS_MAX)) {
    e.maxTokens = `Un número entero entre ${TOKENS_MIN} y ${TOKENS_MAX}, o marca «Sin tope».`;
  }
  if (!esEntero(b.limite) || b.limite < LIMITE_MIN || b.limite > LIMITE_MAX) {
    e.limite = `Un número entero entre ${LIMITE_MIN} y ${LIMITE_MAX}.`;
  }
  return e;
}

/**
 * Lo que HAY que mandar al servidor: solo las claves que cambiaron respecto de lo guardado. De una tarea que cambió
 * se manda el par completo (modelo + temperatura): el servidor valida cada tarea como una unidad.
 * Un borrador inválido no se compara (el botón Guardar ya está bloqueado); devuelve lo que se pueda leer sin inventar.
 */
export function cambiosDelBorrador(b: BorradorAjustes, actual: TwilioAjustes): TwilioSettingsCambio {
  const cambio: TwilioSettingsCambio = {};

  const tareas: NonNullable<TwilioSettingsCambio["tareas"]> = {};
  for (const tarea of ["gasto.parse", "gasto.correct"] as const) {
    const t = b.tareas[tarea];
    const modelo = t.model.trim();
    const temperatura = t.sinTemperatura ? null : t.temperature;
    const previa = actual.tareas[tarea];
    if (modelo !== previa.model || temperatura !== previa.temperature) {
      tareas[tarea] = { model: modelo, temperature: temperatura };
    }
  }
  if (Object.keys(tareas).length > 0) cambio.tareas = tareas;

  // En segundos: lo que ya estaba guardado en una fracción (nadie más escribe esa clave) no cuenta como cambio.
  if (b.timeoutSeg !== null && b.timeoutSeg !== Math.round(actual.timeoutMs / 1000)) {
    cambio.timeoutMs = Math.round(b.timeoutSeg * 1000);
  }

  const tokens = b.sinTopeTokens ? null : b.maxTokens;
  if (tokens !== actual.maxTokensSalida && (b.sinTopeTokens || tokens !== null)) {
    cambio.maxTokensSalida = tokens;
  }

  if (b.limite !== null && b.limite !== actual.limiteMensajesPorMinuto) {
    cambio.limiteMensajesPorMinuto = b.limite;
  }
  if (b.guardarTexto !== actual.guardarTextoEventos) {
    cambio.guardarTextoEventos = b.guardarTexto;
  }
  return cambio;
}

export const hayCambios = (cambio: TwilioSettingsCambio): boolean => Object.keys(cambio).length > 0;

/** ¿Dos borradores son iguales? Para no pisar lo que la persona está editando cuando llegan datos nuevos. */
export function borradoresIguales(a: BorradorAjustes, b: BorradorAjustes): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Llegaron ajustes nuevos del servidor mientras la persona edita: se conserva TODO lo que ella tocó y el resto sigue al
 * servidor. Campo por campo: si el campo del borrador sigue igual que en la base anterior, nadie lo tocó y toma el valor
 * de la base nueva; si es distinto, es una edición suya y se respeta.
 *
 * Sin esto, «Guardar» comparaba el borrador ENTERO contra lo nuevo del servidor y, si otro administrador había cambiado
 * (por ejemplo) el tiempo máximo, mandaba de vuelta el valor viejo y le pisaba el cambio sin que nadie lo notara.
 */
export function fusionarBorrador(previo: BorradorAjustes, baseAnterior: BorradorAjustes, baseNueva: BorradorAjustes): BorradorAjustes {
  const elegir = <T,>(mio: T, viejo: T, nuevo: T): T => (JSON.stringify(mio) === JSON.stringify(viejo) ? nuevo : mio);
  const tarea = (n: NombreTareaIA): BorradorTarea => ({
    model: elegir(previo.tareas[n].model, baseAnterior.tareas[n].model, baseNueva.tareas[n].model),
    sinTemperatura: elegir(previo.tareas[n].sinTemperatura, baseAnterior.tareas[n].sinTemperatura, baseNueva.tareas[n].sinTemperatura),
    temperature: elegir(previo.tareas[n].temperature, baseAnterior.tareas[n].temperature, baseNueva.tareas[n].temperature),
  });
  return {
    tareas: { "gasto.parse": tarea("gasto.parse"), "gasto.correct": tarea("gasto.correct") },
    timeoutSeg: elegir(previo.timeoutSeg, baseAnterior.timeoutSeg, baseNueva.timeoutSeg),
    sinTopeTokens: elegir(previo.sinTopeTokens, baseAnterior.sinTopeTokens, baseNueva.sinTopeTokens),
    maxTokens: elegir(previo.maxTokens, baseAnterior.maxTokens, baseNueva.maxTokens),
    limite: elegir(previo.limite, baseAnterior.limite, baseNueva.limite),
    guardarTexto: elegir(previo.guardarTexto, baseAnterior.guardarTexto, baseNueva.guardarTexto),
  };
}
